/**
 * Hook: useModuleEnabled
 * 
 * Verifica se um módulo está habilitado para uma organização
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface UseModuleEnabledOptions {
  enabled?: boolean;
  staleTime?: number;
}

/**
 * Hook para verificar se um módulo está habilitado
 * 
 * @param orgId - ID da organização
 * @param moduleKey - Chave do módulo (ex: 'fluity_lanches')
 * @param options - Opções do React Query
 * @returns Objeto com isEnabled, isLoading, error
 */
export function useModuleEnabled(
  orgId: string | undefined,
  moduleKey: string,
  options: UseModuleEnabledOptions = {}
) {
  const {
    enabled: queryEnabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutos
  } = options;

  const skipOrgModules = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('org_modules_skip') === '1';

  return useQuery({
    queryKey: ['module-enabled', orgId, moduleKey],
    queryFn: async () => {
      if (!orgId || !moduleKey) return false;

      try {
        const { data, error } = await supabase
          .from('org_modules')
          .select('enabled')
          .eq('org_id', orgId)
          .eq('module_key', moduleKey)
          .maybeSingle();

        // Tratar erros do PostgREST
        if (error) {
          // 404 Not Found - tabela não existe ou recurso não encontrado; evita novas requisições na sessão
          if ((error as any).status === 404 || error.message?.includes('404')) {
            try {
              sessionStorage.setItem('org_modules_skip', '1');
            } catch {}
            return false;
          }
          // Tabela não existe no schema cache (PGRST200/PGRST205) — retornar false silenciosamente
          if (
            error.code === 'PGRST200' ||
            error.code === 'PGRST205' ||
            error.message?.includes('schema cache') ||
            error.message?.includes('Could not find')
          ) {
            try {
              sessionStorage.setItem('org_modules_skip', '1');
            } catch {}
            return false;
          }
          // PGRST116 = "The result contains 0 rows" - esperado quando módulo não está habilitado
          if (error.code === 'PGRST116' || error.message?.includes('0 rows')) {
            return false;
          }
          // 406 Not Acceptable - pode acontecer quando RLS bloqueia ou formato incorreto
          if (error.code === 'PGRST406' || error.message?.includes('406')) {
            return false;
          }
          // Outros erros inesperados
          console.warn('[useModuleEnabled] Error checking module:', error.code, error.message);
          return false;
        }

        // Se não encontrou registro, módulo não está habilitado
        if (!data) {
          return false;
        }

        return data.enabled === true;
      } catch (err) {
        console.error('[useModuleEnabled] Unexpected error:', err);
        return false;
      }
    },
    enabled: !!orgId && queryEnabled && !skipOrgModules,
    staleTime,
    retry: false, // Não retry se módulo não está habilitado
  });
}

/**
 * Hook simplificado que retorna apenas boolean
 */
export function useIsModuleEnabled(
  orgId: string | undefined,
  moduleKey: string
): boolean {
  const { data: isEnabled = false } = useModuleEnabled(orgId, moduleKey);
  return isEnabled;
}
